import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { UnitSystem } from '@/interfaces/units';
import { $http } from '@/main';
import {
  IAgencyCostActivityImperial,
  IAgencyCostImperial,
  IAgencyCostLineItemImperial,
  IAgencyCostMetric,
} from '@/modules/agencyCost/interfaces/agencyCost';
import { AxiosResponse } from 'axios';
import { IPxError, processReason } from '@/lib/util/errors';
import { createNewAgencyCostScenario } from '@/modules/agencyCost/lib/createNew';
import { IBaseScenario } from '@/lib/types/IBaseScenario';
import { IProjectMetadata } from '@/interfaces/report';
import { projectStore } from '@/store/store';
import {
  agencyCostImperialToMetric,
  agencyCostMetricToImperial,
} from '@/modules/agencyCost/lib/unitConversion/agencyScenario';

const endpoint = 'agency';


@Module({
  name: 'agency',
  namespaced: true,
})
export default class AgencyCostStore extends VuexModule {
  /*
  Unit system
   */

  displayUnits: UnitSystem = UnitSystem.Imperial;

  @Mutation
  setDisplayUnits(targetUnitSystem: UnitSystem) {
    this.displayUnits = targetUnitSystem;
  }


  /*
  A central location for http httpErrors to go
   */
  httpErrors: IPxError[] = [];

  @Mutation
  pushHttpErrors(reason: { response: AxiosResponse }) {
    this.httpErrors = processReason(reason);
  }

  @Mutation
  pushError(error: IPxError) {
    this.httpErrors.push(error);
  }

  @Mutation
  clearErrors() {
    this.httpErrors = [];
  }

  /*
  Models
   */


  currentImperial: IAgencyCostImperial = createNewAgencyCostScenario(null);

  // currentImperial = null;

  get currentMetric(): IAgencyCostMetric {
    return agencyCostImperialToMetric(this.currentImperial);
  }

  get currentScenario(): IAgencyCostImperial | IAgencyCostMetric {
    // console.log("Fetching Agency Cost with units:", this.displayUnits);
    let s: any;
    if (this.displayUnits === UnitSystem.Imperial) {
      s = this.currentImperial;
    } else {
      s = this.currentMetric;
    }
    return s;
  }

  @Mutation
  setCurrentScenario(scenario: IAgencyCostImperial | IAgencyCostMetric) {
    if (scenario.unitSystem === UnitSystem.Imperial) {
      // console.log('Setting curr scenario imperial');
      this.currentImperial = scenario;
    } else if (scenario.unitSystem === UnitSystem.Metric) {
      // console.log('Setting curr scenario metric');
      this.currentImperial = agencyCostMetricToImperial(scenario);
    }
  }

  @Action({ rawError: true })
  saveNewScenario(scenario: IAgencyCostImperial) {
    scenario.displayUnits = this.displayUnits;
    // return this.currentImperial
    return $http.post(`project/${endpoint}/new`, scenario).then((response) => {
      this.setCurrentScenario(response.data);
      return response.data;
    }).catch((reason) => {
      console.log(reason);
      this.pushHttpErrors(reason);
      return null;
    });
  }

  @Action({ rawError: true })
  loadCurrentScenario(id: number) {
    console.log(`fetching ${endpoint}`, id);
    return $http.get(`project/${endpoint}/${id}`).then((response) => {
      this.setCurrentScenario(response.data);
      return response.data;
    }).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action({ rawError: true })
  loadScenario(id: number) {
    console.log(`fetching ${endpoint}`, id);
    return $http.get(`project/${endpoint}/${id}`).then((response) => response.data).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action({ rawError: true })
  saveCurrentScenario() {
    this.currentImperial.displayUnits = this.displayUnits;
    console.log(`saving ${endpoint}  to db`, this.currentImperial.id, this.currentImperial.displayUnits, this.currentImperial);
    $http.put(`project/${endpoint}/${this.currentImperial.id}`, this.currentImperial).then(
      (response) => {
        const loadedScenario = response.data;
        loadedScenario.activities.sort((a: IAgencyCostActivityImperial, b: IAgencyCostActivityImperial) => a.yearsAfterStart - b.yearsAfterStart);
        this.setCurrentScenario(response.data);
      },
    ).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action
  deleteScenario(scenario: IBaseScenario) {
    return $http.delete(`/project/${endpoint}/${scenario.id}`).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action({ rawError: true })
  // shareScenario(scenario: IBaseScenario, targetUserEmail: string) {
  shareScenario(data: { scenario: IBaseScenario, targetUserEmail: string }) {
    const { scenario } = data;
    const { targetUserEmail } = data;

    console.log(`Sharing scenario ${scenario.name} with ${targetUserEmail}`);
    return $http.post(`/project/${endpoint}/${scenario.id}/share`, {
      targetUser: targetUserEmail,
    });
  }

  /*
  Defaults
   */

  @Action({ rawError: true })
  loadLineItemDefaults() {
    return $http.get(`/project/${endpoint}/lineitems`).then((response) => {
      const items = response.data;
      return items;
    }).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action({ rawError: true })
  saveNewDefaultLineItem(lineItem: IAgencyCostLineItemImperial) {
    return $http.post(`/project/${endpoint}/lineitems/new`, lineItem).then((response) => {
      const items = response.data;
      return items;
    }).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action({ rawError: true })
  saveLineItem(lineItem: IAgencyCostLineItemImperial) {
    return $http.put(`project/${endpoint}/lineitems/${lineItem.id}`, lineItem).then(
      (response) => response.data,
    ).catch((reason) => this.pushHttpErrors(reason));
  }

  @Action({ rawError: true })
  deleteDefaultLineItem(lineItem: IAgencyCostLineItemImperial) {
    return $http.delete(`/project/${endpoint}/lineitems/${lineItem.id}`).catch((reason) => this.pushHttpErrors(reason));
  }


  /*
  Comparison
   */

  comparisonIds: number[] = [];

  @Mutation
  setComparisonIds(ids: number[]) {
    this.comparisonIds = ids;
  }


  comparisonScenarios: IAgencyCostImperial[] = [];


  @Mutation
  setComparisonScenarios(scenarios: IAgencyCostImperial[]) {
    console.log('setting scenarios', scenarios);
    this.comparisonScenarios = scenarios;
  }

  @Mutation
  addComparisonScenario(scenario: IAgencyCostImperial) {
    this.comparisonScenarios.push(scenario);
  }

  @Mutation
  clearComparisonScenarios() {
    this.comparisonScenarios = [];
  }

  comparisonProject: IProjectMetadata | null = null;

  @Mutation
  setComparisonProject(project: IProjectMetadata | null) {
    this.comparisonProject = project;
  }

  @Action({ rawError: true })
  getComparisonScenarios() {
    this.clearComparisonScenarios();
    this.setComparisonProject(null);

    this.comparisonIds.forEach((id) => {
      this.loadScenario(id).then((scenario) => {
        if (scenario !== null && scenario !== undefined) {
          this.addComparisonScenario(scenario);

          if (this.comparisonProject === null) {
            projectStore.getProjectMetadata(scenario.project)
              .then((meta) => this.setComparisonProject(meta))
              .catch((reason) => this.pushHttpErrors(reason));
          }
        }
      }).catch((reason) => console.debug(reason));
    });
  }
}
